/**
 * 模型清单（/models/manifest.json）的契约定义与加载
 *
 * manifest 与模型文件同置于 R2 bucket，由 Functions 代理到同域 /models/。
 * 上传脚本 tools/upload-models.mjs 是 manifest 的唯一生产者。
 */

export interface ModelEntry {
  /** R2 中的对象 key，如 "chalkgpt_holds.onnx" */
  file: string;
  /** 文件字节数（进度条分母与完整性校验） */
  bytes: number;
  /** 十六进制小写 SHA-256，下载后强制校验 */
  sha256: string;
  /** 用途标签：detector / sam-encoder / sam-decoder */
  kind: string;
  /** 可选模型（如人物检测），默认不阻塞主流程 */
  optional?: boolean;
}

export interface ModelManifest {
  version: number;
  generatedAt: string;
  /** 模型直链前缀（entry 无绝对 url 时 url = base + file） */
  base?: string;
  /** 备用直链前缀（主源失败时按序回退，T10） */
  mirrors?: string[];
  models: Record<string, ModelEntry>;
}

export class ManifestError extends Error {}

/**
 * 本应用可接受的最低 manifest 版本。模型清单换血（如 person-detector
 * picodet → yolo11n）必须 bump 版本：localStorage 离线回退副本若低于该值
 * 则拒绝使用——否则会按废弃清单下载废弃模型（#15 第三轮：手机/桌面均以
 * 旧 picodet 清单建会话，feeds 名 `images` 对不上 picodet 输入 `image`，
 * 「可选模型」把整条线路识别炸成不可用）。
 */
export const MANIFEST_MIN_VERSION = 2;

const LOCAL_CACHE_KEY = "grip-models:manifest";

function readLocalCopy(): ModelManifest | undefined {
  try {
    const raw = localStorage.getItem(LOCAL_CACHE_KEY);
    if (!raw) return undefined;
    return JSON.parse(raw) as ModelManifest;
  } catch {
    return undefined;
  }
}

function writeLocalCopy(m: ModelManifest): void {
  try {
    localStorage.setItem(LOCAL_CACHE_KEY, JSON.stringify(m));
  } catch {
    // localStorage 不可用（隐私模式等）：静默放弃，下次在线时再缓存
  }
}

function validate(data: unknown): ModelManifest {
  const m = data as ModelManifest;
  if (
    typeof m !== "object" ||
    m === null ||
    typeof m.version !== "number" ||
    typeof m.models !== "object" ||
    m.models === null
  ) {
    throw new ManifestError("manifest 结构非法");
  }
  if (m.version < MANIFEST_MIN_VERSION) {
    // 版本低于应用预期：清单已过期（指向已废弃的模型），按非法处理
    throw new ManifestError(
      `manifest 版本过旧（v${m.version} < v${MANIFEST_MIN_VERSION}，模型清单已更新）`,
    );
  }
  for (const [id, entry] of Object.entries(m.models)) {
    if (
      !/^[a-f0-9]{64}$/.test(entry.sha256) ||
      !Number.isSafeInteger(entry.bytes) ||
      entry.bytes <= 0 ||
      typeof entry.file !== "string"
    ) {
      throw new ManifestError(`manifest 条目非法：${id}`);
    }
  }
  return m;
}

/**
 * 拉取并校验 manifest。成功后写入 localStorage 副本；
 * 网络失败/HTTP 错误/结构非法时回退到本地副本（断网刷新的可用性前提），
 * 无本地副本才抛错。
 */
export async function loadManifest(
  url = "/models/manifest.json",
  fetchImpl: typeof fetch = fetch,
): Promise<ModelManifest> {
  try {
    const resp = await fetchImpl(url, { cache: "no-store" });
    if (!resp.ok) {
      throw new ManifestError(
        `manifest 拉取失败：HTTP ${resp.status}（R2 未启用或未上传模型？）`,
      );
    }
    const m = validate(await resp.json());
    writeLocalCopy(m);
    return m;
  } catch (e) {
    const cached = readLocalCopy();
    if (cached) {
      try {
        const v = validate(cached);
        console.warn("[grip] manifest 在线拉取失败，使用本地副本（离线回退）");
        return v;
      } catch (ve) {
        // 本地副本过期/非法：绝不静默沿用废弃模型清单（#15 第三轮根因）
        throw new ManifestError(
          `manifest 拉取失败（${String(e instanceof Error ? e.message : e)}），` +
            `且本地副本不可用（${ve instanceof Error ? ve.message : String(ve)}）。` +
            "请检查网络后刷新页面",
        );
      }
    }
    if (e instanceof ManifestError) throw e;
    throw new ManifestError(`manifest 拉取失败（离线且无本地副本）：${String(e)}`);
  }
}
